/**
 * Das Tagesraster des Grundkalenders: Kopfzeile, Band der Kanalregeln und Apartmentzeilen.
 * Bearbeitet wird im Monat je Apartment; hier wird gewählt und hinübergereicht.
 *
 * Nur dieses Raster scrollt horizontal; die Apartmentspalte bleibt links verankert
 * (FRONTEND_GUIDELINES §7/§8).
 */
import { useMemo } from 'react';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import type {
  AvailabilityDistributionApartment,
  AvailabilityDistributionChannelCloseout,
} from '@/shared/api';
import { ApartmentRow } from './ApartmentRow';
import { CloseoutBandRow } from './CloseoutBandRow';
import { DayHeaderRow } from './DayHeaderRow';
import { buildApartmentModel } from './calendarModel';
import {
  APARTMENT_COLUMN,
  APARTMENT_COLUMN_MOBILE,
  DAY_COLUMN_MIN,
  DAY_COLUMN_MIN_MOBILE,
} from './calendarGeometry';
import type { SelectedRange, SelectionMode } from './rangeSelectionModel';

interface CalendarGridProps {
  apartments: AvailabilityDistributionApartment[];
  closeouts: AvailabilityDistributionChannelCloseout[];
  today: string;
  selectedPropertyId: string | null;
  ranges: readonly SelectedRange[];
  canEdit: boolean;
  onBegin: (propertyId: string, date: string, mode: SelectionMode, immediate?: boolean) => void;
  onExtend: (date: string) => void;
  /** Der Apartmentname führt in den Monat dieses Apartments. */
  onOpenMonth?: (propertyId: string) => void;
  /** Zimmer, deren Gesamtwohnung direkt darüber steht – sie werden eingerückt. */
  childIds?: ReadonlySet<string>;
  /** „verwalten“ im Band führt in den Reiter Kanalregeln. */
  onManageRules?: () => void;
  /** Namen aller Apartments des Objekts – das Band nennt auch ausgefilterte. */
  apartmentNames?: Map<string, string>;
}

export function CalendarGrid({
  apartments,
  closeouts,
  today,
  selectedPropertyId,
  ranges,
  canEdit,
  onBegin,
  onExtend,
  onOpenMonth,
  childIds,
  onManageRules,
  apartmentNames,
}: CalendarGridProps) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const sidebarWidth = mobile ? APARTMENT_COLUMN_MOBILE : APARTMENT_COLUMN;
  const dayMin = mobile ? DAY_COLUMN_MIN_MOBILE : DAY_COLUMN_MIN;
  const models = useMemo(
    () => apartments.map((apartment) => buildApartmentModel(apartment, closeouts)),
    [apartments, closeouts],
  );
  const names = useMemo(
    () => new Map(apartments.map((apartment) => [apartment.propertyId, apartment.name])),
    [apartments],
  );
  const dates = models[0]?.dates ?? [];

  return (
    <div
      tabIndex={0}
      role="region"
      aria-label="Verfügbarkeitskalender, horizontal scrollbar"
      className="focus-visible:ring-forest/40 w-full max-w-full overflow-x-auto overscroll-x-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
    >
      <div className="min-w-max">
        <DayHeaderRow dates={dates} today={today} sidebarWidth={sidebarWidth} dayMin={dayMin} />
        <CloseoutBandRow
          closeouts={closeouts}
          dates={dates}
          apartmentNames={apartmentNames ?? names}
          totalApartments={apartmentNames?.size ?? apartments.length}
          sidebarWidth={sidebarWidth}
          dayMin={dayMin}
          onManage={onManageRules}
        />
        {apartments.map((apartment, index) => (
          <ApartmentRow
            key={apartment.propertyId}
            apartment={apartment}
            model={models[index]!}
            today={today}
            sidebarWidth={sidebarWidth}
            dayMin={dayMin}
            selection={apartment.propertyId === selectedPropertyId ? ranges : []}
            child={childIds?.has(apartment.propertyId)}
            onOpenMonth={onOpenMonth}
            onBegin={canEdit ? onBegin : undefined}
            onExtend={canEdit ? onExtend : undefined}
          />
        ))}
      </div>
    </div>
  );
}
